import React from 'react'
import { VStack } from '@chakra-ui/react'
import { IncidentSubmission } from '../../../../types/MonthlyReport'
import {
  injuryOptions,
  surgeryOptions,
  stressOptions,
} from '../utils/incidentOptions'
import IncidentGateway from './IncidentGateway'
import IncidentCategoryCard from './IncidentCategoryCard'
import TagCloud from './TagCloud'
import HospitalSubForm from './HospitalSubForm'
import { illnessOptions } from '../utils/incidentOptions'

interface IncidentSectionProps {
  value: IncidentSubmission
  onChange: (value: IncidentSubmission) => void
  readOnly?: boolean
}

const defaultCategoryState: Omit<IncidentSubmission, 'gateway'> = {
  injury: { reported: null, items: [] },
  surgery: { reported: null, items: [] },
  illness: { reported: null, items: [] },
  stress: { reported: null, items: [] },
  hospital: {
    reported: null,
    erVisit: null,
    hospitalised: null,
    daysHospitalised: null,
  },
}

const IncidentSection: React.FC<IncidentSectionProps> = ({
  value,
  onChange,
}) => {
  const handleGatewayChange = (gw: 'yes' | 'no' | null) => {
    if (gw === 'no' || gw === null) {
      onChange({ ...defaultCategoryState, gateway: gw })
      return
    }
    onChange({ ...value, gateway: gw })
  }

  const handleInjuryReported = (reported: boolean | null) => {
    const items = reported === true ? (value.injury?.items ?? []) : []
    onChange({ ...value, injury: { reported, items } })
  }

  const handleInjuryTags = (items: string[]) => {
    onChange({
      ...value,
      injury: { reported: value.injury?.reported ?? null, items },
    })
  }

  const handleSurgeryReported = (reported: boolean | null) => {
    const items = reported === true ? (value.surgery?.items ?? []) : []
    onChange({ ...value, surgery: { reported, items } })
  }

  const handleSurgeryTags = (items: string[]) => {
    onChange({
      ...value,
      surgery: { reported: value.surgery?.reported ?? null, items },
    })
  }

  const handleIllnessReported = (reported: boolean | null) => {
    const items = reported === true ? (value.illness?.items ?? []) : []
    onChange({ ...value, illness: { reported, items } })
  }

  const handleIllnessTags = (items: string[]) => {
    onChange({
      ...value,
      illness: { reported: value.illness?.reported ?? null, items },
    })
  }

  const handleStressReported = (reported: boolean | null) => {
    const items = reported === true ? (value.stress?.items ?? []) : []
    onChange({ ...value, stress: { reported, items } })
  }

  const handleStressTags = (items: string[]) => {
    onChange({
      ...value,
      stress: { reported: value.stress?.reported ?? null, items },
    })
  }

  const handleHospitalReported = (reported: boolean | null) => {
    if (reported !== true) {
      onChange({
        ...value,
        hospital: {
          reported,
          erVisit: null,
          hospitalised: null,
          daysHospitalised: null,
        },
      })
      return
    }
    onChange({
      ...value,
      hospital: { ...value.hospital!, reported },
    })
  }

  const handleErVisit = (erVisit: boolean | null) => {
    onChange({
      ...value,
      hospital: { ...value.hospital!, erVisit },
    })
  }

  const handleHospitalised = (hospitalised: boolean | null) => {
    const daysHospitalised =
      hospitalised === true ? (value.hospital?.daysHospitalised ?? null) : null
    onChange({
      ...value,
      hospital: { ...value.hospital!, hospitalised, daysHospitalised },
    })
  }

  const handleDaysHospitalised = (days: number | null) => {
    onChange({
      ...value,
      hospital: { ...value.hospital!, daysHospitalised: days },
    })
  }

  return (
    <VStack align='stretch' spacing={4}>
      <IncidentGateway value={value.gateway} onChange={handleGatewayChange} />
      {value.gateway === 'yes' && (
        <VStack align='stretch' spacing={3}>
          <IncidentCategoryCard
            title='Injury'
            descriptor='Did you have an injury?'
            reported={value.injury?.reported ?? null}
            onReportedChange={handleInjuryReported}
            colorScheme='red'
          >
            <TagCloud
              options={injuryOptions.options}
              selected={value.injury?.items ?? []}
              onChange={handleInjuryTags}
            />
          </IncidentCategoryCard>

          <IncidentCategoryCard
            title='Surgery'
            descriptor='Did you have surgery?'
            reported={value.surgery?.reported ?? null}
            onReportedChange={handleSurgeryReported}
            colorScheme='orange'
          >
            <TagCloud
              options={surgeryOptions.options}
              selected={value.surgery?.items ?? []}
              onChange={handleSurgeryTags}
            />
          </IncidentCategoryCard>

          <IncidentCategoryCard
            title='Illness'
            descriptor='Did you have an illness?'
            reported={value.illness?.reported ?? null}
            onReportedChange={handleIllnessReported}
            colorScheme='blue'
          >
            <TagCloud
              options={illnessOptions.options}
              selected={value.illness?.items ?? []}
              onChange={handleIllnessTags}
            />
          </IncidentCategoryCard>

          <IncidentCategoryCard
            title='Significant Stress'
            descriptor='Did you experience significant stress?'
            reported={value.stress?.reported ?? null}
            onReportedChange={handleStressReported}
            colorScheme='teal'
          >
            <TagCloud
              options={stressOptions.options}
              selected={value.stress?.items ?? []}
              onChange={handleStressTags}
            />
          </IncidentCategoryCard>

          <IncidentCategoryCard
            title='Hospital / Emergency'
            descriptor='Did you visit a hospital or ER?'
            reported={value.hospital?.reported ?? null}
            onReportedChange={handleHospitalReported}
            colorScheme='red'
          >
            <HospitalSubForm
              erVisit={value.hospital?.erVisit ?? null}
              hospitalised={value.hospital?.hospitalised ?? null}
              daysHospitalised={value.hospital?.daysHospitalised ?? null}
              onErVisitChange={handleErVisit}
              onHospitalisedChange={handleHospitalised}
              onDaysHospitalisedChange={handleDaysHospitalised}
            />
          </IncidentCategoryCard>
        </VStack>
      )}
    </VStack>
  )
}

export default IncidentSection
